import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Slider, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import { Pause, PlayArrow } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const WAVEFORM_BARS = 40;
const PLAYBACK_SPEEDS = [1, 1.5, 2];

const formatTime = (seconds) => {
  if (seconds === undefined || Number.isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const formatSpeed = (speed) => `${speed}x`;

/** Alturas pseudo-aleatórias estáveis por URL — só visual, estilo WhatsApp. */
function buildWaveformHeights(seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return Array.from({ length: WAVEFORM_BARS }, (_, i) => {
    const n = Math.abs(Math.sin((hash + i * 17) * 0.31)) * 0.65 + 0.35;
    return Math.round(n * 100) / 100;
  });
}

const TeamAudioMessage = ({ audioUrl, isOwn = false, message, onAudioPlayed }) => {
  const { t: tChat } = useTranslation("chat");
  const { t: tTeam } = useTranslation("team");
  const theme = useTheme();
  const audioRef = useRef(null);
  const playedReportedRef = useRef(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speedIndex, setSpeedIndex] = useState(0);

  const playbackRate = PLAYBACK_SPEEDS[speedIndex];

  const waveformHeights = useMemo(
    () => buildWaveformHeights(String(audioUrl || "audio")),
    [audioUrl]
  );

  useEffect(() => {
    if (!audioUrl || !audioRef.current) return;
    const el = audioRef.current;
    playedReportedRef.current = false;
    setSpeedIndex(0);
    el.playbackRate = 1;

    const onLoadedMetadata = () => setDuration(el.duration);
    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
    };

    el.addEventListener("loadedmetadata", onLoadedMetadata);
    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("ended", onEnded);
    if (el.duration && !Number.isNaN(el.duration)) setDuration(el.duration);

    return () => {
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("ended", onEnded);
    };
  }, [audioUrl]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const palette = isOwn
    ? {
        icon: theme.palette.primary.contrastText,
        iconBg: alpha(theme.palette.common.white, 0.22),
        iconBgHover: alpha(theme.palette.common.white, 0.32),
        textMuted: alpha(theme.palette.primary.contrastText, 0.78),
        track: theme.palette.common.white,
        barActive: alpha(theme.palette.common.white, 0.95),
        barInactive: alpha(theme.palette.common.white, 0.34),
        speedBg: alpha(theme.palette.common.white, 0.16),
        speedBgHover: alpha(theme.palette.common.white, 0.26),
        speedText: alpha(theme.palette.primary.contrastText, 0.92),
      }
    : {
        icon: theme.palette.primary.main,
        iconBg: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.12),
        iconBgHover: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.32 : 0.2),
        textMuted: theme.palette.text.secondary,
        track: theme.palette.primary.main,
        barActive: theme.palette.primary.main,
        barInactive: alpha(theme.palette.primary.main, 0.24),
        speedBg: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.08),
        speedBgHover: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.28 : 0.14),
        speedText: theme.palette.primary.main,
      };

  const reportPlayed = () => {
    if (playedReportedRef.current) return;
    playedReportedRef.current = true;
    onAudioPlayed?.(message);
  };

  const togglePlay = async () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    try {
      await audioRef.current.play();
      setPlaying(true);
      reportPlayed();
    } catch (err) {
      console.error("[TeamAudioMessage] play:", err);
    }
  };

  const cycleSpeed = () => {
    setSpeedIndex((prev) => (prev + 1) % PLAYBACK_SPEEDS.length);
  };

  const handleSeek = (_, value) => {
    if (!audioRef.current || duration <= 0) return;
    const pct = Array.isArray(value) ? value[0] : value;
    const sec = (pct / 100) * duration;
    audioRef.current.currentTime = sec;
    setCurrentTime(sec);
  };

  const displayTime = playing || currentTime > 0 ? formatTime(currentTime) : formatTime(duration);
  const speedLabel = formatSpeed(playbackRate);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.875,
        width: "100%",
        minWidth: 220,
        maxWidth: 320,
        py: 0.1,
      }}
    >
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      <Tooltip title={playing ? tChat("input.audioPreviewPause") : tChat("input.audioPreviewPlay")}>
        <IconButton
          size="small"
          onClick={togglePlay}
          sx={{
            color: palette.icon,
            flexShrink: 0,
            width: 36,
            height: 36,
            bgcolor: palette.iconBg,
            "&:hover": { bgcolor: palette.iconBgHover },
          }}
          aria-label={playing ? tChat("input.audioPreviewPause") : tChat("input.audioPreviewPlay")}
        >
          {playing ? <Pause sx={{ fontSize: 20 }} /> : <PlayArrow sx={{ fontSize: 20 }} />}
        </IconButton>
      </Tooltip>

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.4 }}>
        <Box
          sx={{
            position: "relative",
            height: 24,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              gap: "1.5px",
              px: 0.15,
              pointerEvents: "none",
            }}
          >
            {waveformHeights.map((h, i) => {
              const barProgress = (i / WAVEFORM_BARS) * 100;
              const active = barProgress <= progress;
              return (
                <Box
                  key={i}
                  sx={{
                    flex: 1,
                    minWidth: 1.5,
                    maxWidth: 3,
                    height: `${Math.round(h * 16 + 5)}px`,
                    borderRadius: 1,
                    bgcolor: active ? palette.barActive : palette.barInactive,
                    transition: "background-color 0.1s ease",
                  }}
                />
              );
            })}
          </Box>

          <Slider
            size="small"
            value={progress}
            onChange={handleSeek}
            sx={{
              width: "100%",
              m: 0,
              p: 0,
              height: 24,
              color: palette.track,
              opacity: 0.001,
              "& .MuiSlider-rail, & .MuiSlider-track, & .MuiSlider-thumb": {
                opacity: 0,
              },
            }}
            aria-label={tChat("input.audioSeekAria")}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.75,
            minHeight: 16,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontVariantNumeric: "tabular-nums",
              fontWeight: 600,
              fontSize: "0.6875rem",
              lineHeight: 1,
              color: palette.textMuted,
              userSelect: "none",
            }}
          >
            {displayTime}
          </Typography>

          <Tooltip title={tTeam("audioSpeedTooltip")}>
            <Box
              component="button"
              type="button"
              onClick={cycleSpeed}
              aria-label={tTeam("audioSpeedLabel", { speed: speedLabel })}
              sx={{
                border: "none",
                cursor: "pointer",
                flexShrink: 0,
                minWidth: 34,
                px: 0.75,
                py: 0.2,
                borderRadius: 10,
                bgcolor: palette.speedBg,
                color: palette.speedText,
                fontSize: "0.625rem",
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.2,
                fontFamily: "inherit",
                transition: "background-color 0.12s ease",
                "&:hover": { bgcolor: palette.speedBgHover },
              }}
            >
              {speedLabel}
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default TeamAudioMessage;
